import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import {
  Ticket,
  Menu,
  X,

  LogOut,
  User,
  Compass,
  Wallet as WalletIcon,
  Repeat,
  LayoutDashboard,
  ScanLine,
  Building2,
  Users,
  Banknote,
  ShieldCheck,
  UserPlus,
} from "lucide-react";
import { useState, type ComponentType } from "react";
import { useAuth } from "@/hooks/useAuth";
import { NotificationBell } from "@/components/NotificationBell";
import { ZyntraLogo } from "@/components/ZyntraLogo";
import { usePermissions } from "@/lib/permissions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";

type NavItem = { to: any; label: string; icon: ComponentType<{ className?: string }> };
type NavSection = { title: string | null; items: NavItem[] };

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, signOut } = useAuth();
  const perms = usePermissions();

  // Build sectioned nav based on permissions
  const sections: NavSection[] = [];

  // Public
  sections.push({
    title: null,
    items: [{ to: "/", label: "Explorar", icon: Compass }],
  });

  // User account (only when logged in)
  if (user) {
    sections.push({
      title: "Cuenta",
      items: [
        { to: "/my-tickets", label: "Mis Entradas", icon: Ticket },
        { to: "/resale", label: "Reventa", icon: Repeat },
        { to: "/wallet", label: "Wallet", icon: WalletIcon },
      ],
    });
  } else {
    // Logged-out users still see resale browse
    sections[0].items.push({ to: "/resale", label: "Reventa", icon: Repeat });
  }

  // Organizer / staff
  if (user) {
    const orgItems: NavItem[] = [];
    if (perms.belongsToAnyTenant || perms.isLegacyOrganizer || perms.isPlatformAdmin) {
      orgItems.push({ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard });
    }
    orgItems.push({ to: "/validate", label: "Validar", icon: ScanLine });
    if (orgItems.length > 0) {
      sections.push({ title: "Organización", items: orgItems });
    }

    if (!perms.loading && !perms.canCreateEventsAnywhere && !perms.hasPendingRequest) {
      sections.push({
        title: null,
        items: [
          {
            to: "/request-organizer-access",
            label: "Solicitar acceso",
            icon: UserPlus,
          },
        ],
      });
    }
  }

  // Admin
  if (perms.isPlatformAdmin) {
    sections.push({
      title: "Administración",
      items: [
        { to: "/admin/tenants", label: "Tenants", icon: Building2 },
        { to: "/admin/users", label: "Usuarios", icon: Users },
        { to: "/admin/payouts", label: "Payouts", icon: Banknote },
        { to: "/admin/resale", label: "Reventa (admin)", icon: Repeat },
        { to: "/admin/refunds", label: "Reembolsos", icon: Banknote },
        { to: "/dashboard/admin", label: "Panel admin", icon: ShieldCheck },
      ],
    });
  }

  // Desktop: estructura agrupada
  const primaryLinks: NavItem[] = [
    { to: "/", label: "Explorar", icon: Compass },
    { to: "/resale", label: "Reventa", icon: Repeat },
    ...(user ? [{ to: "/my-tickets", label: "Mis Entradas", icon: Ticket }] : []),
  ];
  const accountLinks: NavItem[] = user
    ? [
        { to: "/my-tickets", label: "Mis Entradas", icon: Ticket },
        { to: "/wallet", label: "Wallet", icon: WalletIcon },
        ...(!perms.loading && !perms.canCreateEventsAnywhere && !perms.hasPendingRequest
          ? [{ to: "/request-organizer-access", label: "Solicitar acceso de organizador", icon: UserPlus }]
          : []),
      ]
    : [];
  const groupMenus = sections.filter(
    (sec) => sec.title === "Organización" || sec.title === "Administración",
  ).map((sec) => ({
    title: sec.title === "Administración" ? "Admin" : sec.title!,
    items: sec.title === "Administración"
      ? sec.items.map((i) => (i.to === "/admin/resale" ? { ...i, label: "Reventa (admin)" } : i.to === "/dashboard/admin" ? { ...i, label: "Panel admin" } : i))
      : sec.items,
  }));


  async function handleSignOut() {
    setConfirmLogoutOpen(false);
    await signOut();
    setMobileOpen(false);
    navigate({ to: "/" });
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" className="flex items-center" aria-label="Zyntra Tickets — Inicio">
          <ZyntraLogo className="h-10 w-auto sm:h-11" />
        </Link>

        {/* Desktop: enlaces principales + menús agrupados */}
        <nav className="hidden items-center gap-1 lg:flex" aria-label="Principal">
          {primaryLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                location.pathname === link.to
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {link.label}
            </Link>
          ))}
          {groupMenus.map((g) => {
            const active = g.items.some((i) => location.pathname.startsWith(i.to));
            return (
              <DropdownMenu key={g.title}>
                <DropdownMenuTrigger
                  className={`flex items-center gap-1 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium outline-none transition-colors ${
                    active ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {g.title} <ChevronDown className="h-3.5 w-3.5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-52">
                  {g.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <DropdownMenuItem key={item.to} asChild>
                        <Link to={item.to} className="flex cursor-pointer items-center gap-2">
                          <Icon className="h-4 w-4" /> {item.label}
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          })}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          {!loading && user ? (
            <>
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-2 rounded-lg bg-secondary px-3 py-1.5 outline-none">
                  <User className="h-3.5 w-3.5 text-primary" />
                  <span className="max-w-[140px] truncate text-xs font-medium text-secondary-foreground">
                    {user.user_metadata?.full_name || user.email}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="truncate text-xs font-normal text-muted-foreground">{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {accountLinks.map((item) => {
                    const Icon = item.icon;
                    return (
                      <DropdownMenuItem key={item.to} asChild>
                        <Link to={item.to} className="flex cursor-pointer items-center gap-2">
                          <Icon className="h-4 w-4" /> {item.label}
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => setConfirmLogoutOpen(true)} className="flex cursor-pointer items-center gap-2 text-destructive focus:text-destructive">
                    <LogOut className="h-4 w-4" /> Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Link
              to="/login"
              className="rounded-lg gradient-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-all hover:opacity-90"
            >
              Ingresar
            </Link>
          )}
        </div>

        <button
          className="rounded-lg p-2 text-muted-foreground lg:hidden"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"}
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="border-t border-border lg:hidden glass flex flex-col max-h-[calc(100vh-4rem)]">
          {/* User identity strip */}
          {user && (
            <div className="flex items-center gap-3 border-b border-border/60 px-4 py-3 shrink-0">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary">
                <User className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">
                  {user.user_metadata?.full_name || "Mi cuenta"}
                </p>
                <p className="truncate text-xs text-muted-foreground">{user.email}</p>
              </div>
              <NotificationBell />
            </div>
          )}

          <div className="px-3 py-3 space-y-4 flex-1 overflow-y-auto">
            {sections.map((section, idx) => (
              <div key={idx}>
                {section.title && (
                  <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                    {section.title}
                  </p>
                )}
                <div className="space-y-0.5">
                  {section.items.map((item, itemIdx) => {
                    const Icon = item.icon;
                    const active = location.pathname === item.to;
                    return (
                      <Link
                        key={`${idx}-${itemIdx}-${item.to}`}
                        to={item.to}
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors ${
                          active
                            ? "bg-accent text-accent-foreground"
                            : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                        }`}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
                {idx < sections.length - 1 && (
                  <div className="mt-3 border-t border-border/40" />
                )}
              </div>
            ))}
          </div>

          <div className="border-t border-border/60 px-3 py-3 shrink-0 bg-background/95 backdrop-blur">
            {user ? (
              <button
                onClick={() => setConfirmLogoutOpen(true)}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive transition-colors hover:bg-destructive/20 active:scale-[0.99]"
              >
                <LogOut className="h-4 w-4" />
                Cerrar sesión
              </button>
            ) : (
              <Link
                to="/login"
                onClick={() => setMobileOpen(false)}
                className="block rounded-lg gradient-primary px-4 py-3 text-center text-sm font-semibold text-primary-foreground"
              >
                Ingresar
              </Link>
            )}
          </div>
        </div>
      )}

      <AlertDialog open={confirmLogoutOpen} onOpenChange={setConfirmLogoutOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cerrar sesión?</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a salir de tu cuenta. Podrás volver a ingresar cuando quieras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSignOut}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Cerrar sesión
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </header>
  );
}
